import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Toast from '../../components/Toast';
import CustomSelect from '../../components/common/CustomSelect';
import { settingsApi } from '../../services/api';

const REQUIRE_2FA_OPTIONS = [
  { value: 'off', label: 'Off (optional)' },
  { value: 'admins', label: 'For administrators only' },
  { value: 'all', label: 'For all users' },
];

export default function AdminSecurity() {
  const [policy, setPolicy] = useState({
    min_length: 8,
    max_length: 128,
    require_uppercase: true,
    require_lowercase: true,
    require_digits: true,
    require_special: false,
    max_repeated_chars: 4,
  });
  const [lockout, setLockout] = useState({ max_failed_attempts: 10 });
  const [require2fa, setRequire2fa] = useState('off');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLockout, setSavingLockout] = useState(false);
  const [savingRequire2fa, setSavingRequire2fa] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const navigate = useNavigate();

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([settingsApi.getSecurity(), settingsApi.getSecurityLockout(), settingsApi.getSecurityRequire2fa()])
      .then(([secRes, lockRes, req2faRes]) => {
        if (!cancelled) {
          if (secRes.data) setPolicy(secRes.data);
          if (lockRes.data) setLockout(lockRes.data);
          if (req2faRes.data && req2faRes.data.require_2fa) setRequire2fa(req2faRes.data.require_2fa);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load security settings');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSavePolicy = async () => {
    setSaving(true);
    setError(null);
    try {
      await settingsApi.putSecurity(policy);
      showToast('success', 'Password policy saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save password policy');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLockout = async () => {
    setSavingLockout(true);
    setError(null);
    try {
      await settingsApi.putSecurityLockout(lockout);
      showToast('success', 'Account lockout policy saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save lockout policy');
    } finally {
      setSavingLockout(false);
    }
  };

  const handleSaveRequire2fa = async () => {
    setSavingRequire2fa(true);
    setError(null);
    try {
      await settingsApi.putSecurityRequire2fa(require2fa);
      showToast('success', 'Require 2FA policy saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save Require 2FA policy');
    } finally {
      setSavingRequire2fa(false);
    }
  };

  return (
    <Layout>
      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-eucnet-navy">Security</h1>
            <p className="mt-2 text-eucnet-grey-600">
              Configure password policy, account lockout, and two-factor authentication requirements.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Password policy */}
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 border-l-4 border-l-amber-500">
            <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Password policy</h2>
            <p className="text-sm text-eucnet-grey-600 mb-4">
              Define minimum length, character requirements, and other password rules for signup and password changes.
            </p>
            {loading ? (
              <p className="text-sm text-eucnet-grey-500">Loading…</p>
            ) : (
              <div className="space-y-4 max-w-xl">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Minimum length</label>
                    <input
                      type="number"
                      min={6}
                      max={64}
                      value={policy.min_length}
                      onChange={(e) => setPolicy(p => ({ ...p, min_length: parseInt(e.target.value, 10) || 8 }))}
                      className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Maximum length</label>
                    <input
                      type="number"
                      min={32}
                      max={256}
                      value={policy.max_length}
                      onChange={(e) => setPolicy(p => ({ ...p, max_length: parseInt(e.target.value, 10) || 128 }))}
                      className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Max repeated characters</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={policy.max_repeated_chars}
                    onChange={(e) => setPolicy(p => ({ ...p, max_repeated_chars: parseInt(e.target.value, 10) || 4 }))}
                    className="w-full max-w-[8rem] border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex flex-wrap gap-6 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policy.require_uppercase}
                      onChange={(e) => setPolicy(p => ({ ...p, require_uppercase: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">Require uppercase</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policy.require_lowercase}
                      onChange={(e) => setPolicy(p => ({ ...p, require_lowercase: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">Require lowercase</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policy.require_digits}
                      onChange={(e) => setPolicy(p => ({ ...p, require_digits: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">Require digit</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policy.require_special}
                      onChange={(e) => setPolicy(p => ({ ...p, require_special: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">Require special character</span>
                  </label>
                </div>
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleSavePolicy}
                    disabled={saving}
                    className="px-6 py-2.5 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save password policy'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Account lockout */}
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 border-l-4 border-l-rose-500">
            <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Account lockout</h2>
            <p className="text-sm text-eucnet-grey-600 mb-4">
              Lock user accounts after a number of failed login attempts. Locked accounts can be unlocked by password reset or by an administrator.
            </p>
            {loading ? (
              <p className="text-sm text-eucnet-grey-500">Loading…</p>
            ) : (
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-40">
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Max failed attempts</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={lockout.max_failed_attempts}
                    onChange={(e) => setLockout(l => ({ ...l, max_failed_attempts: parseInt(e.target.value, 10) || 10 }))}
                    className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSaveLockout}
                  disabled={savingLockout}
                  className="px-6 py-2.5 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50"
                >
                  {savingLockout ? 'Saving…' : 'Save lockout policy'}
                </button>
              </div>
            )}
          </div>

          {/* Require 2FA */}
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 border-l-4 border-l-eucnet-sky">
            <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Two-factor authentication (2FA)</h2>
            <p className="text-sm text-eucnet-grey-600 mb-4">
              Users enable 2FA (e.g. Google Authenticator) in <strong>My Profile → Security</strong>. Choose whether 2FA is required for admins or all users.
            </p>
            {loading ? (
              <p className="text-sm text-eucnet-grey-500">Loading…</p>
            ) : (
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[220px]">
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Require 2FA</label>
                  <CustomSelect
                    value={require2fa}
                    onChange={(e) => setRequire2fa(e.target.value)}
                    options={REQUIRE_2FA_OPTIONS}
                    placeholder="Off (optional)"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSaveRequire2fa}
                  disabled={savingRequire2fa}
                  className="px-6 py-2.5 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50"
                >
                  {savingRequire2fa ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>

          {/* Other security */}
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6">
            <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Other security settings</h2>
            <p className="text-sm text-eucnet-grey-600">
              IP allowlists, API keys, and additional security controls will be available here.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
